import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Award, Lightbulb, Globe2, Leaf } from "lucide-react";
import img1 from "../Images/Ind.jpeg";

const Description = () => {
  const stats = [
    { label: "International Markets", value: "29+", description: "Countries served worldwide" },
    { label: "Years Experience", value: "15+", description: "Industry expertise" },
    { label: "Successful Projects", value: "500+", description: "Completed installations" },
    { label: "Client Satisfaction", value: "98%", description: "Customer approval rating" },
  ];

  const features = [
    {
      icon: <Lightbulb className="w-6 h-6 text-blue-600" />,
      title: "Innovation Focus",
      description: "Cutting-edge R&D and technological advancement in industrial energy solutions",
    },
    {
      icon: <Globe2 className="w-6 h-6 text-blue-600" />,
      title: "Global Reach",
      description: "Operating across Nepal and 29+ international markets with proven expertise",
    },
    {
      icon: <Leaf className="w-6 h-6 text-blue-600" />,
      title: "Sustainability",
      description: "Committed to environmental preservation through eco-friendly energy solutions",
    },
    {
      icon: <Award className="w-6 h-6 text-blue-600" />,
      title: "Excellence",
      description: "Industry-leading solutions and exceptional service delivery",
    },
  ];

  // Schema for organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gortherm Energy",
    "description": "Nepal's trusted utility partner leading industrial and commercial sectors with advanced solutions in boilers, steam accessories, water treatment, and pollution control",
    "foundingDate": "2015",
    "url": "https://www.gorthermenergy.com",
    "logo": "https://www.gorthermenergy.com/logo.png",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Biratnagar",
      "addressCountry": "NP"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Nepal and International Markets"
    },
    "knowsAbout": [
      "Industrial Boilers",
      "Steam Accessories",
      "Water Treatment",
      "Pollution Control",
      "Energy Solutions",
      "Process Engineering"
    ]
  };

  return (
    <>
      <Helmet>
        {/* Structured data for this section */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema, null, 2)}
        </script>
      </Helmet>

      <section className="w-full bg-white py-16 md:py-24" aria-label="Company overview and achievements">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Text Content */}
            <article className="space-y-6 md:space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="inline-block"
                >
                  <span className="bg-gradient-to-r from-sky-800 to-sky-500 text-white px-4 py-2 rounded-full text-xs sm:text-sm font-semibold">
                    Established 2015
                  </span>
                </motion.div>

                <header>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                    <span className="text-blue-600">Innovative Industrial Solutions</span> for{" "}
                    <span className="relative inline-block">
                      <span className="relative z-10">Energy Excellence</span>
                      <motion.span
                        initial={{ width: "0%" }}
                        whileInView={{ width: "100%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="absolute bottom-1 sm:bottom-2 left-0 h-2 sm:h-3 bg-gradient-to-r from-red-100 to-blue-100 z-0"
                        aria-hidden="true"
                      />
                    </span>
                  </h2>
                </header>

                <div className="space-y-4">
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    Nepal's Leading Industrial Energy Solutions Provider
                  </h3>
                  
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    As Nepal's trusted utility partner, <strong className="font-bold text-red-600">Gortherm</strong><strong className="font-bold text-blue-600">Energy</strong> leads the industrial and commercial sectors with advanced solutions in <strong>industrial boilers</strong>, <strong>steam accessories</strong>, <strong>water treatment systems</strong>, and <strong>pollution control equipment</strong>.
                  </p>
                  
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    Since 2015, we have served over 500 successful projects across 29 international markets, combining cutting-edge research and development with global technology partnerships. Our commitment to <strong>sustainability</strong> and <strong>innovation</strong> enables businesses to enhance productivity while contributing to environmental preservation.
                  </p>
                  
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    With 8+ years of industry expertise and a 98% client satisfaction rate, we deliver industry-leading <strong>process engineering services</strong>, <strong>energy optimization solutions</strong>, and comprehensive technical support across Nepal and international markets.
                  </p>
                </div>
              </motion.div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="space-y-2 p-4 rounded-lg hover:bg-gray-50 transition-colors duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex-shrink-0" aria-hidden="true">
                        {feature.icon}
                      </div>
                      <h4 className="font-semibold text-gray-900 text-base sm:text-lg">
                        {feature.title}
                      </h4>
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </article>

            {/* Image and Stats */}
            <aside className="relative mt-12 lg:mt-0">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative h-[400px] sm:h-[500px] md:h-[600px] w-full overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src={img1}
                    alt="Gortherm Energy industrial facility showing advanced boiler systems and steam accessories installation in Nepal"
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    width="600"
                    height="600"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" aria-hidden="true"></div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -z-10 top-4 sm:top-8 right-4 sm:right-8 w-full h-full bg-gradient-to-r from-red-100 to-blue-100 rounded-2xl" aria-hidden="true"></div>

                {/* Stats Overlay */}
                <div className="absolute -bottom-6 sm:-bottom-8 left-4 right-4 sm:left-8 sm:right-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
                  >
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {stats.map((stat, index) => (
                        <motion.div
                          key={stat.label}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                          className="text-center p-2"
                        >
                          <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">
                            {stat.value}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 font-medium">
                            {stat.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 hidden sm:block">
                            {stat.description}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </aside>
          </div>

          {/* Additional SEO Content Section */}
          <div className="mt-24 md:mt-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl p-6 sm:p-8 md:p-12"
            >
              <h3 className="text-2xl sm:text-3xl font-bold text-center mb-6 md:mb-8 text-gray-900">
                Comprehensive Industrial Energy Solutions
              </h3>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900">Industrial Boiler Systems</h4>
                  <p className="text-sm sm:text-base text-gray-700">
                    Complete boiler sales, installation, and maintenance services with advanced automation and control systems for optimal efficiency.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900">Steam Accessories Supply</h4>
                  <p className="text-sm sm:text-base text-gray-700">
                    Premium quality steam traps, valves, strainers, and accessories from trusted manufacturers for reliable steam system performance.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900">Water Treatment Solutions</h4>
                  <p className="text-sm sm:text-base text-gray-700">
                    Advanced water treatment systems including softeners, filtration, and chemical dosing for industrial and commercial applications.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900">Process Engineering Services</h4>
                  <p className="text-sm sm:text-base text-gray-700">
                    Expert process optimization, energy audits, and system design services to maximize operational efficiency and reduce costs.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900">Pollution Control Equipment</h4>
                  <p className="text-sm sm:text-base text-gray-700">
                    Eco-friendly pollution control solutions ensuring compliance with environmental regulations while maintaining productivity.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900">24/7 Technical Support</h4>
                  <p className="text-sm sm:text-base text-gray-700">
                    Round-the-clock technical assistance, emergency response, and preventive maintenance programs across all service locations.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Description;